import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Box, GlobalStyles, Typography } from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { fetchProgressReport, previewSubline, ProgressReportFetchError, STRINGS, type ProgressReport } from './report';
import { chosenBlocks, feedbackParts, flowBlocks, layOut, type FeedbackPart, type FlowBlock, type Measured, type Placed, type SectionId } from './flow';
import { paragraphLines } from './lines';
import {
  AccessSection,
  Finding,
  HistorySection,
  FeedbackSection,
  NextStepsSection,
  NothingYet,
  PageFrame,
  PageHeader,
  SamplesPair,
  sheetText,
  SkillsSection,
  SpeakingPage,
  StudentBanner,
  WritingPage,
} from './pages';
import { REPORT } from './tokens';
import { recordReportViewed, usePrintRecorder } from './usage';
import { FeedbackSidetab } from '../ProgressReports/FeedbackSidetab';
import { PRINT_PATH } from '../ProgressReports/index';
import { NoBenchmarkNotice, PreviewToolbar, type ReportNotice } from '../ProgressReports/PreviewToolbar';
import { PrintSectionsDialog, sectionIds, usePrintSections } from '../ProgressReports/PrintSectionsDialog';
import { currentIdentity, onProgressReportsReset, type ProgressReportSession } from '../ProgressReports/students';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; report: ProgressReport }
  | { kind: 'missing' }
  | { kind: 'forbidden' }
  | { kind: 'error' };

type Loaded = { studentId: string; result: Exclude<LoadState, { kind: 'loading' }> };

function outcomeOf(err: unknown): Exclude<LoadState, { kind: 'loading' | 'ready' }> {
  if (err instanceof ProgressReportFetchError && err.status === 404) return { kind: 'missing' };
  if (err instanceof ProgressReportFetchError && err.status === 403) return { kind: 'forbidden' };
  return { kind: 'error' };
}

export const printStyles = (
  <GlobalStyles
    styles={{
      '@page': { size: 'letter', margin: 0 },
      '@media print': {
        'html, body': { margin: 0, background: '#FFFFFF' },
        '.report-nav, .report-toolbar, .report-measure': { display: 'none !important' },
        '.report-dialog': { display: 'none !important' },
        '.report-shell': { display: 'block !important', background: '#FFFFFF !important', minHeight: '0 !important' },
        '.report-stage': { display: 'block !important', background: '#FFFFFF !important', padding: '0 !important' },
        '.report-page': { boxShadow: 'none !important', margin: '0 !important', breakAfter: 'page', breakInside: 'avoid' },
        '.report-page:last-of-type': { breakAfter: 'auto' },
        '*': { printColorAdjust: 'exact', WebkitPrintColorAdjust: 'exact' },
      },
    }}
  />
);

const ChevronLeft = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M15.4 18.6 8.8 12l6.6-6.6 1.4 1.4-5.2 5.2 5.2 5.2Z" fill="currentColor" />
  </svg>
);

const PrintIcon = () => (
  <svg width={20} height={20} viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M16 8V5H8v3H6V3h12v5h-2ZM4 10h16v7h-3v4H7v-4H4v-7Zm5 9h6v-5H9v5Zm-3-5h1v-2h10v2h1v-2H6v2Zm11.5-1.5a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
      fill="currentColor"
    />
  </svg>
);

type StateStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export function ProgressReportPage({
  load = fetchProgressReport,
  listSource,
  storage,
  sectionsStorage,
  identity = currentIdentity,
}: {
  load?: (studentId: string) => Promise<ProgressReport>;
  listSource?: () => Promise<ProgressReportSession>;
  storage?: StateStorage | null;
  sectionsStorage?: StateStorage | null;
  identity?: () => Promise<string>;
}) {
  const { studentId = '' } = useParams();
  const navigate = useNavigate();
  const fromProfile = (useLocation().state as { from?: unknown } | null)?.from === 'profile';
  const sections = usePrintSections(identity, sectionsStorage);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [generation, setGeneration] = useState(0);
  const [pageCount, setPageCount] = useState<number | null>(null);

  useEffect(
    () =>
      onProgressReportsReset(() => {
        setLoaded(null);
        setPageCount(null);
        setGeneration((n) => n + 1);
      }),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    load(studentId)
      .then((report) => {
        if (cancelled) return;
        setLoaded({ studentId, result: { kind: 'ready', report } });
        recordReportViewed();
      })
      .catch((err: unknown) => {
        if (!cancelled) setLoaded({ studentId, result: outcomeOf(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [load, studentId, generation]);

  const state: LoadState = loaded && loaded.studentId === studentId ? loaded.result : { kind: 'loading' };
  const ready = state.kind === 'ready';
  usePrintRecorder('single', ready ? 1 : 0);

  return (
    <Box className="report-shell" sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: REPORT.screenBg }}>
      {printStyles}
      <Box className="report-nav" sx={{ display: 'contents' }}>
        <Sidebar sections={SIDEBAR_SECTIONS} />
      </Box>
      <Box component="main" sx={{ flex: 1, minWidth: 0, overflowX: 'auto', fontFamily: REPORT.chromeFont }}>
        {fromProfile ? (
          <ProfileToolbar
            ready={ready}
            title={ready ? state.report.student.fullName || 'Student' : 'Student progress report'}
            subline={ready ? previewSubline(state.report, pageCount) : ''}
            notice={ready ? reportNotice(state.report) : null}
            onBack={() => navigate(`/roster/${encodeURIComponent((ready && state.report.student.key) || studentId)}`)}
            onPrint={sections.open}
          />
        ) : (
          <PreviewToolbar
            studentId={ready ? state.report.student.listKey || state.report.student.key : null}
            title={ready ? state.report.student.fullName || 'Student' : 'Student progress report'}
            subline={ready ? previewSubline(state.report, pageCount) : ''}
            notice={ready ? reportNotice(state.report) : null}
            listSource={listSource}
            storage={storage}
            sectionsStorage={sectionsStorage}
            identity={identity}
          />
        )}
        {fromProfile && sections.pending !== null && (
          <PrintSectionsDialog
            open
            title={`Print ${(ready && state.report.student.firstName) || STRINGS.theStudent}'s report`}
            groupLabel="Include in the report"
            primaryLabel="Preview report"
            chosen={sections.pending.chosen}
            onCancel={sections.cancel}
            onConfirm={(chosen) => {
              sections.save(chosen);
              navigate(PRINT_PATH, { state: { from: 'profile', studentId, sections: sectionIds(chosen) } });
            }}
          />
        )}
        <Box className="report-stage" sx={{ padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>
          {ready ? <ReportPages report={state.report} onPageCount={setPageCount} /> : <StateNotice state={state} />}
        </Box>
      </Box>
      {ready && <FeedbackSidetab />}
    </Box>
  );
}

function reportNotice(report: ProgressReport): ReportNotice | null {
  if (report.benchmarks.hasData || report.samples.writing.length > 0 || report.samples.speaking.length > 0) return null;
  return { firstName: report.student.firstName, hasAccess: report.access !== null };
}

function ProfileToolbar({
  ready,
  title,
  subline,
  notice,
  onBack,
  onPrint,
}: {
  ready: boolean;
  title: string;
  subline: string;
  notice: ReportNotice | null;
  onBack: () => void;
  onPrint: () => void;
}) {
  return (
<Box
  className="report-toolbar"
  sx={{
    bgcolor: '#FFFFFF',
    borderBottom: `1px solid ${REPORT.divider}`,
    padding: '20px 24px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  }}
>
  <Box>
    <Box
      component="button"
      type="button"
      onClick={onBack}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: 0,
        border: 0,
        background: 'none',
        cursor: 'pointer',
        color: REPORT.navy,
        fontFamily: 'inherit',
        fontSize: 14,
        lineHeight: '20px',
        fontWeight: 600,
        '&:hover': { textDecoration: 'underline' },
      }}
    >
      <ChevronLeft />
      Back to the student profile
    </Box>
  </Box>
  <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '16px' }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <Typography component="h1" sx={{ fontSize: 28, lineHeight: '36px', fontWeight: 600, color: REPORT.ink }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 500, color: REPORT.muted, minHeight: 20 }}>
        {subline}
      </Typography>
    </Box>
    <Box
      component="button"
      type="button"
      disabled={!ready}
      onClick={onPrint}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        height: 40,
        padding: '0 20px',
        border: 0,
        borderRadius: '8px',
        bgcolor: REPORT.navy,
        color: '#FFFFFF',
        fontFamily: 'inherit',
        fontSize: 16,
        lineHeight: '20px',
        fontWeight: 600,
        cursor: 'pointer',
        flexShrink: 0,
        '&:disabled': { bgcolor: 'rgba(8, 8, 27, 0.12)', color: 'rgba(8, 8, 27, 0.26)', cursor: 'default' },
      }}
    >
      <PrintIcon />
      Print report
    </Box>
  </Box>
  {notice !== null && <NoBenchmarkNotice notice={notice} />}
</Box>
  );
}

function StateNotice({ state }: { state: LoadState }) {
  const message =
    state.kind === 'loading'
      ? 'Preparing the progress report…'
      : state.kind === 'missing'
        ? 'This student is not in the classified roster yet, so there is no report to prepare.'
        : state.kind === 'forbidden'
          ? 'You do not have access to this student’s report.'
          : 'The report could not be prepared right now. Try again in a moment.';
  return (
    <Box sx={{ width: REPORT.sheet.width, maxWidth: '100%', bgcolor: '#FFFFFF', p: 4, borderRadius: '8px', boxShadow: REPORT.cardShadow }}>
      <Typography data-testid="report-state" sx={{ fontSize: 16, color: REPORT.muted }}>
        {message}
      </Typography>
    </Box>
  );
}

function BlockView({ kind, report, pieces }: { kind: FlowBlock; report: ProgressReport; pieces?: FeedbackPart[] }) {
  switch (kind) {
    case 'banner':
      return <StudentBanner report={report} />;
    case 'nothing':
      return <NothingYet report={report} />;
    case 'history':
      return <HistorySection report={report} />;
    case 'skills':
      return <SkillsSection report={report} />;
    case 'finding':
      return <Finding report={report} />;
    case 'access':
      return <AccessSection report={report} />;
    case 'writing':
      return <WritingPage report={report} />;
    case 'speaking':
      return <SpeakingPage report={report} />;
    case 'samples':
      return <SamplesPair report={report} />;
    case 'nextSteps':
      return <NextStepsSection report={report} />;
    default:
      return pieces ? <FeedbackSection report={report} pieces={pieces} /> : null;
  }
}

function Block({ kind, report, pieces, measure = false }: { kind: FlowBlock; report: ProgressReport; pieces?: FeedbackPart[]; measure?: boolean }) {
  return (
    <Box data-measure={measure ? kind : undefined} sx={{ display: 'flex', flexDirection: 'column' }}>
      <BlockView kind={kind} report={report} pieces={pieces} />
    </Box>
  );
}

// Feedback pieces that land on the same page print as one section.
function grouped(placed: Placed[]): { kind: FlowBlock; pieces?: FeedbackPart[] }[] {
  const out: { kind: FlowBlock; pieces?: FeedbackPart[] }[] = [];
  placed.forEach(({ kind, part }) => {
    const last = out[out.length - 1];
    if (part && last?.pieces) last.pieces.push(part);
    else out.push(part ? { kind, pieces: [part] } : { kind });
  });
  return out;
}

const BODY_HEIGHT = REPORT.sheet.height - 2 * REPORT.sheet.padding;

function measuredHeight(root: HTMLElement, name: string): number {
  const el = root.querySelector<HTMLElement>(`[data-measure="${name}"]`);
  return el ? el.getBoundingClientRect().height : 0;
}

// Probes for the labels around a page break inside the feedback, measured to
// learn how much height each label adds.
const PROBE = { key: 'feedback:probe' as const, runs: [], opensSection: false, opensParagraph: false, closesSection: false, continued: false, continuesTo: null };
const MORE_PROBE: FeedbackPart = { ...PROBE, continuesTo: 99 };
const CONTINUED_PROBE: FeedbackPart = { ...PROBE, continued: true };

// Lays the report's sections onto letter pages. Each section is first drawn
// off-screen at sheet width to measure it, then the pages are filled in order:
// a section that does not fit on the current page moves whole to the next,
// except the feedback, which fills the page and continues on the next.
// The measure runs again once the report font has loaded, since a fallback
// font wraps text differently.
export function ReportPages({
  report,
  sections,
  onPageCount,
}: {
  report: ProgressReport;
  sections?: ReadonlySet<SectionId>;
  onPageCount?: (pages: number) => void;
}) {
  const measured = useMemo(() => (sections ? chosenBlocks(flowBlocks(report), sections) : flowBlocks(report)), [report, sections]);
  const parts = useMemo(() => (measured.includes('feedback') ? feedbackParts(report) : []), [measured, report]);
  const [layout, setLayout] = useState<{ measured: FlowBlock[]; pages: Placed[][] } | null>(null);
  const measurer = useRef<HTMLDivElement>(null);
  const pages = layout !== null && layout.measured === measured ? layout.pages : null;

  useEffect(() => {
    const fonts = typeof document === 'undefined' ? undefined : document.fonts;
    if (!fonts) return;
    let live = true;
    void fonts.ready.then(() => {
      if (live) flushSync(() => setLayout(null));
    });
    return () => {
      live = false;
    };
  }, []);

  useLayoutEffect(() => {
    const root = measurer.current;
    if (pages !== null || root === null) return;
    const capacity = BODY_HEIGHT - measuredHeight(root, 'header');
    const items = measured.flatMap((kind): Measured[] =>
      kind === 'feedback'
        ? parts.map((part) => {
            const block = root.querySelector<HTMLElement>(`[data-measure="${part.key}"]`);
            return { kind: part.key, height: measuredHeight(root, part.key), part, lines: block ? paragraphLines(block) : [] };
          })
        : [{ kind, height: measuredHeight(root, kind) }],
    );
    const extra = { more: measuredHeight(root, 'feedback:more'), continued: measuredHeight(root, 'feedback:continued') };
    setLayout({ measured, pages: layOut(items, capacity, extra) });
  }, [pages, measured, parts]);

  const count = pages?.length ?? null;
  useLayoutEffect(() => {
    if (count !== null) onPageCount?.(count);
  }, [count, onPageCount]);

  if (pages === null) {
    return (
      <Box
        ref={measurer}
        className="report-measure"
        aria-hidden="true"
        sx={{ position: 'fixed', left: -10000, top: 0, width: REPORT.sheet.inner, visibility: 'hidden', display: 'flex', flexDirection: 'column', ...sheetText }}
      >
        <Box data-measure="header">
          <PageHeader report={report} page={1} pageCount={1} />
        </Box>
        {measured
          .filter((kind) => kind !== 'feedback')
          .map((kind) => (
            <Block key={kind} kind={kind} report={report} measure />
          ))}
        {parts.map((part) => (
          <Block key={part.key} kind={part.key} report={report} pieces={[part]} measure />
        ))}
        {parts.length > 0 && (
          <>
            <Block kind="feedback:more" report={report} pieces={[MORE_PROBE]} measure />
            <Block kind="feedback:continued" report={report} pieces={[CONTINUED_PROBE]} measure />
          </>
        )}
      </Box>
    );
  }

  return (
    <>
      {pages.map((placed, i) => (
        <PageFrame key={i} report={report} page={i + 1} pageCount={pages.length}>
          {grouped(placed).map(({ kind, pieces }) => (
            <Block key={kind} kind={kind} report={report} pieces={pieces} />
          ))}
        </PageFrame>
      ))}
    </>
  );
}
